/**
 * Phase 24.4a — minimal media query hook.
 *
 * Used by meal_plan tab (S4a.2) to conditionally compute default-expanded
 * accordion state based on viewport width. Single source of truth pattern
 * (Round 2 Decision 1 follow-up): all responsive state flows through this
 * hook, no parallel CSS-only desktop expansion that conflicts with
 * Zustand state on resize.
 *
 * Usage:
 *   const isDesktop = useMediaQuery("(min-width: 1024px)");
 */
import { useEffect, useState } from "react";

export function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState<boolean>(() => {
    if (typeof window === "undefined" || !window.matchMedia) return false;
    return window.matchMedia(query).matches;
  });

  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mql = window.matchMedia(query);
    const handler = (e: MediaQueryListEvent) => setMatches(e.matches);
    // Set initial in case SSR snapshot diverged
    setMatches(mql.matches);
    mql.addEventListener("change", handler);
    return () => mql.removeEventListener("change", handler);
  }, [query]);

  return matches;
}
